import { inject, Injectable, signal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private _prefix = signal('SMM-');
  private _isBrowser = typeof window !== 'undefined';

  /**
   * Gets the value stored in local storage for the specified key.
   *
   * @param key The key to retrieve the value for.
   * @returns The value stored in local storage for the specified key, or null
   * if no value is found.
   */
  getItem<T>(key: string): T | null {
    if (!this._isBrowser) return null;

    const data = window.localStorage.getItem(this._prefix() + key);
    if (data === null) return null;

    try {
      return JSON.parse(data) as T;
    } catch {
      console.error(`Error parsing localStorage data for key: ${key}`);
      return null;
    }
  }

  /**
   * Sets the value for the specified key in local storage.
   *
   * @param key The key to set the value for.
   * @param value The value to store in local storage.
   */
  setItem(key: string, value: unknown): void {
    if (!this._isBrowser) return;

    try {
      const data = JSON.stringify(value);
      window.localStorage.setItem(this._prefix() + key, data);
    } catch (error) {
      console.error(`Error setting localStorage item for key: ${key}`, error);
    }
  }

  /**
   * Removes all items in local storage.
   */
  clear(): void {
    if (!this._isBrowser) return;

    try {
      window.localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage', error);
    }
  }

  /**
   * Removes a specific item from local storage.
   *
   * @param key The key of the item to remove.
   */
  removeItem(key: string): void {
    if (!this._isBrowser) return;

    try {
      window.localStorage.removeItem(this._prefix() + key);
    } catch (error) {
      console.error(`Error removing localStorage item for key: ${key}`, error);
    }
  }

  /**
   * Changes the prefix used for all localStorage keys.
   *
   * @param newPrefix The new prefix to use.
   */
  changePrefix(newPrefix: string): void {
    this._prefix.set(newPrefix);
  }
}
